import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createPhotoBoothAPI } from '@photobooth/api-client';
import { getEffectiveApiBaseUrl } from '../../lib/config/terminal-urls';
import { ProductList } from './components/ProductList';
import { CustomerForm, type CustomerInfo } from './components/CustomerForm';
import { PaymentScreen } from './components/PaymentScreen';
import { LogoutModal } from '../../components/LogoutModal';
import type { LocalProduct } from '../../lib/db/schema';
import { useOutletAuth } from '../../lib/auth/OutletAuthProvider';

const apiClient = createPhotoBoothAPI({
  baseUrl: getEffectiveApiBaseUrl(),
});

type OrderStep = 'product-selection' | 'customer-info' | 'payment' | 'complete';

export default function OrderTerminal() {
  const { outlet, authState, clearAuthentication } = useOutletAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<OrderStep>('product-selection');
  const [selectedProduct, setSelectedProduct] = useState<LocalProduct | null>(null);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [voucherCode, setVoucherCode] = useState<string | undefined>(undefined);
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const handleProductSelect = (product: LocalProduct) => {
    setSelectedProduct(product);
    setCurrentStep('customer-info');
  };

  const handleCustomerSubmit = async (info: CustomerInfo, code?: string) => {
    if (!authState?.outletId || !selectedProduct) return;

    setCustomerInfo(info);
    setVoucherCode(code);

    try {
      const response = await apiClient.transactions.createOrder({
        outletId: authState.outletId,
        productId: selectedProduct.productId,
        customer: {
          name: info.name,
          phone: info.phone,
          email: info.email,
          instagram: info.instagram,
        },
        paymentMethod: 'cash',
        markAsPaid: false,
        voucherCode: code,
      });

      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to create order');
      }

      setTransactionId(response.data.transactionId);
      setCurrentStep('payment');
    } catch (err) {
      console.error('Failed to create order:', err);
      throw err;
    }
  };

  const handlePaymentSuccess = (transactionId: string) => {
    console.log('Payment successful:', transactionId);
    setCurrentStep('complete');
  };

  const handleBackToProducts = () => {
    setSelectedProduct(null);
    setCustomerInfo(null);
    setTransactionId(null);
    setCurrentStep('product-selection');
  };

  const handleBackToCustomerInfo = () => {
    setTransactionId(null);
    setCurrentStep('customer-info');
  };

  const handleStartNewOrder = () => {
    setSelectedProduct(null);
    setCustomerInfo(null);
    setVoucherCode(undefined);
    setTransactionId(null);
    setCurrentStep('product-selection');
  };

  return (
    <div className="min-h-screen p-8 bg-gradient-to-br from-pink-50 to-purple-50">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
              Order
            </h1>
            {outlet && (
              <p className="text-gray-600 mt-1 text-lg">
                {outlet.name} - {outlet.location}
              </p>
            )}
            {selectedProduct && currentStep !== 'complete' && (
              <p className="text-gray-600 mt-2">
                Selected: {selectedProduct.name}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="px-6 py-3 rounded-2xl bg-white text-gray-700 shadow-lg hover:shadow-xl transition-all"
            >
              ← Back to Menu
            </Link>
            <button
              type="button"
              onClick={() => setShowLogoutModal(true)}
              className="px-6 py-3 rounded-2xl bg-red-500 text-white shadow-lg hover:bg-red-600 hover:shadow-xl transition-all font-semibold"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Step Indicator */}
        {currentStep !== 'complete' && (
          <div className="mb-8 flex items-center justify-center gap-4">
            <div className={`flex items-center gap-2 ${currentStep === 'product-selection' ? 'text-pink-500 font-bold' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'product-selection' ? 'bg-pink-500 text-white' : 'bg-gray-200'}`}>
                1
              </div>
              <span>Select Product</span>
            </div>
            <div className="w-12 h-1 bg-gray-200" />
            <div className={`flex items-center gap-2 ${currentStep === 'customer-info' ? 'text-pink-500 font-bold' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'customer-info' ? 'bg-pink-500 text-white' : 'bg-gray-200'}`}>
                2
              </div>
              <span>Customer Info</span>
            </div>
            <div className="w-12 h-1 bg-gray-200" />
            <div className={`flex items-center gap-2 ${currentStep === 'payment' ? 'text-pink-500 font-bold' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'payment' ? 'bg-pink-500 text-white' : 'bg-gray-200'}`}>
                3
              </div>
              <span>Payment</span>
            </div>
          </div>
        )}

        {/* Content */}
        {currentStep === 'product-selection' && (
          <ProductList onProductSelect={handleProductSelect} />
        )}

        {currentStep === 'customer-info' && selectedProduct && outlet && (
          <CustomerForm
            productId={selectedProduct.productId}
            outletId={outlet.outletId}
            productPrice={selectedProduct.price}
            onSubmit={handleCustomerSubmit}
            onBack={handleBackToProducts}
          />
        )}

        {currentStep === 'payment' && selectedProduct && customerInfo && transactionId && (
          <PaymentScreen
            product={selectedProduct}
            customerInfo={customerInfo}
            transactionId={transactionId}
            voucherCode={voucherCode}
            onSuccess={handlePaymentSuccess}
            onBack={handleBackToCustomerInfo}
          />
        )}

        {currentStep === 'complete' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
              <div className="text-8xl mb-6">🎉</div>
              <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
                Order Complete!
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Thank you for your order. Please proceed to Capture to take your photos.
              </p>
              <button
                onClick={handleStartNewOrder}
                className="px-8 py-4 rounded-2xl bg-gradient-to-r from-pink-500 to-purple-500 text-white text-lg font-semibold hover:shadow-xl transition-all"
              >
                Start New Order
              </button>
            </div>
          </div>
        )}
      </div>

      {showLogoutModal && authState?.outletId && (
        <LogoutModal
          outletId={authState.outletId}
          terminalName="Order"
          onClose={() => setShowLogoutModal(false)}
          onLogout={async () => {
            await clearAuthentication();
            navigate('/auth');
          }}
        />
      )}
    </div>
  );
}
